/**
 * The chart kit's arithmetic, pure: round axis ticks, a value's share of a scale, which category labels to print, the
 * running totals of a waterfall and the arcs of a donut. No React and no DOM, so it is tested on its own and every
 * chart draws from the same numbers.
 */
/** A round step for an axis: 1, 2, 2.5 or 5 times a power of ten, at least `rough`. */
export function niceStep(rough, integer = false) {
    if (!(rough > 0) || !Number.isFinite(rough))
        return 1;
    const power = 10 ** Math.floor(Math.log10(rough));
    let step = 10 * power;
    for (const multiple of [1, 2, 2.5, 5, 10]) {
        if (multiple * power >= rough) {
            step = multiple * power;
            break;
        }
    }
    if (integer)
        return Math.max(1, Math.ceil(step));
    return step;
}
/**
 * The ticks of a value axis that always starts at zero (or below it, when a value is negative): about `count` round
 * steps that cover `values`. `[0, 5000, 10000, 15000]` for a maximum of ₹12,400 (in rupees, not paise: the caller
 * scales). Counts pass `integer` so a scale of three sessions never reads 0.5.
 */
export function niceTicks(values, count = 4, integer = false) {
    const finite = values.filter((value) => Number.isFinite(value));
    const max = Math.max(0, ...finite);
    const min = Math.min(0, ...finite);
    const span = max - min;
    const step = niceStep(span === 0 ? 1 : span / count, integer);
    const bottom = min < 0 ? -Math.ceil(-min / step) * step : 0;
    const top = max <= 0 ? (min < 0 ? 0 : step) : Math.ceil(max / step) * step;
    const ticks = [];
    for (let tick = bottom; tick <= top + step / 1e6; tick += step)
        ticks.push(Math.round(tick * 1e6) / 1e6);
    return ticks;
}
/** A value's position on a scale from `lo` to `hi`, 0 … 1, clamped. */
export function shareOf(value, lo, hi) {
    if (!(hi > lo))
        return 0;
    return Math.min(1, Math.max(0, (value - lo) / (hi - lo)));
}
/**
 * Which category labels an axis prints so that at most `room` show: every n-th from the first, and always the last
 * (dropping the one just before it when the two would touch).
 */
export function axisLabels(count, room) {
    if (count <= 0)
        return [];
    const every = Math.max(1, Math.ceil(count / Math.max(1, room)));
    const shown = Array.from({ length: count }, (_, index) => index % every === 0);
    const last = count - 1;
    if (!shown[last]) {
        shown[last] = true;
        const before = last - (last % every);
        if (before !== last && last - before < Math.ceil(every / 2) + 1 && before !== 0)
            shown[before] = false;
    }
    return shown;
}
/**
 * The bars of a waterfall: a `total` step stands on zero and resets the running figure; any other step floats from the
 * running figure by its signed value. `lo` and `hi` are the scale that holds every bar (zero always inside).
 */
export function waterfallOf(steps) {
    let running = 0;
    const bars = steps.map((step) => {
        if (step.total === true) {
            running = step.value;
            return { from: 0, to: step.value, running };
        }
        const from = running;
        running += step.value;
        return { from, to: running, running };
    });
    const ends = bars.flatMap((bar) => [bar.from, bar.to]);
    return { bars, lo: Math.min(0, ...ends), hi: Math.max(0, ...ends) };
}
/** The arcs of a donut from its values; negative and zero values take no arc. */
export function donutArcs(values) {
    const positive = values.map((value) => (Number.isFinite(value) && value > 0 ? value : 0));
    const total = positive.reduce((sum, value) => sum + value, 0);
    let start = 0;
    return positive.map((value) => {
        const share = total === 0 ? 0 : value / total;
        const arc = { start, share };
        start += share;
        return arc;
    });
}
//# sourceMappingURL=scale.js.map